import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

export const EXTERNAL_MAILBOX_CONFIGURATIONS_URL = '/external_mailbox_configurations';
export const EXTERNAL_MAILBOX_CONFIGURATIONS_QUERY_KEY = ['externalMailboxConfigurations'];

const ExternalMailboxConfigurationSchema = z.object({
  id: z.number(),
  // A shared mailbox stores both addresses in one backslash separated string, and a
  // Microsoft mailbox has no login until its token exchange completes.
  login: z.string().nullable(),
  isMicrosoft: z.boolean(),
  host: z.string().nullable(),
  port: z.number().nullable(),
  ssl: z.boolean().nullable(),
  signature: z.string().nullable(),
  reauthUri: z.string().nullable(),
});

export type ExternalMailboxConfiguration = z.infer<typeof ExternalMailboxConfigurationSchema>;

const LoadExternalMailboxConfigurationsResponseSchema = z.object({
  data: z.array(ExternalMailboxConfigurationSchema),
});

export type LoadExternalMailboxConfigurationsResponse = z.infer<
  typeof LoadExternalMailboxConfigurationsResponseSchema
>;

export async function loadExternalMailboxConfigurationsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(EXTERNAL_MAILBOX_CONFIGURATIONS_URL);

  const parseResult = LoadExternalMailboxConfigurationsResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'externalMailboxConfigurations');
    throw new SilentError();
  }

  return parseResult.data;
}

const EMPTY_MAILBOXES: LoadExternalMailboxConfigurationsResponse = { data: [] };

export function useLoadExternalMailboxConfigurations(
  options: QueryOptions<LoadExternalMailboxConfigurationsResponse> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<LoadExternalMailboxConfigurationsResponse, ApiError>({
    queryKey: EXTERNAL_MAILBOX_CONFIGURATIONS_QUERY_KEY,
    queryFn: () => loadExternalMailboxConfigurationsQueryFn(axiosInstance),
    placeholderData: previousData => (isDefined(previousData) ? previousData : EMPTY_MAILBOXES),
    ...options,
  });

  const result = addResourceNameToQueryResult<
    LoadExternalMailboxConfigurationsResponse,
    ApiError,
    'externalMailboxConfigurations'
  >('externalMailboxConfigurations', queryResult);

  // placeholderData only covers the pending state: a request that fails before ever
  // succeeding would leave the card mapping over an undefined list.
  return {
    ...result,
    externalMailboxConfigurations: result.externalMailboxConfigurations ?? EMPTY_MAILBOXES,
  };
}
